import { Checkbox } from "@/view/components/ui/checkbox";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

interface Option {
  value: string;
  label: string;
  commission?: number;
  subOptions?: Option[];
}

interface UpdateTablesProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
}

export const UpdateTables = ({
  options,
  value,
  onChange,
}: UpdateTablesProps) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const toggle = (option: Option, checked: boolean, parent?: Option) => {
    if (checked) {
      if (parent?.value.toUpperCase() === "FGTS" && parent.subOptions) {
        const siblingValues = parent.subOptions.map((s) => s.value);
        const withoutSiblings = value.filter((v) => !siblingValues.includes(v));

        onChange([...withoutSiblings, option.value]);
        return;
      }

      if (!value.includes(option.value)) {
        onChange([...value, option.value]);
      }
    } else {
      onChange(value.filter((v) => v !== option.value));
    }
  };

  const toggleGroup = (group: Option) => {
    setOpen((prev) => ({
      ...prev,
      [group.value]: !prev[group.value],
    }));
  };

  const hasChecked = (subs?: Option[]) =>
    subs?.some((s) => value.includes(s.value));

  return (
    <div className="flex flex-col gap-2">
      <h1>Selecione as tabelas</h1>

      <ul className="flex flex-col gap-2">
        {options.map((option) => {
          const isGroup = !!option.subOptions?.length;
          const checked = isGroup
            ? hasChecked(option.subOptions)
            : value.includes(option.value);

          return (
            <li
              key={option.value}
              className={`rounded-lg border ${
                checked ? "border-primary" : "border-gray-200"
              }`}
            >
              {isGroup ? (
                <>
                  <div
                    className="flex cursor-pointer items-center justify-between p-3"
                    onClick={() => toggleGroup(option)}
                  >
                    <span className="text-sm font-medium">{option.label}</span>

                    {open[option.value] ? (
                      <ChevronUp className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    )}
                  </div>

                  {open[option.value] && (
                    <ul>
                      {option.subOptions!.map((sub) => (
                        <li
                          key={sub.value}
                          className="flex cursor-pointer items-center justify-between p-3 hover:bg-gray-100"
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={value.includes(sub.value)}
                              onCheckedChange={(checked) =>
                                toggle(sub, Boolean(checked), option)
                              }
                            />

                            <span>{sub.label}</span>
                          </div>

                          {sub.commission !== undefined && (
                            <span className="text-sm text-gray-500">
                              {sub.commission}%
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={value.includes(option.value)}
                      onCheckedChange={(checked) =>
                        toggle(option, Boolean(checked))
                      }
                    />

                    <span>{option.label}</span>
                  </div>

                  {option.commission !== undefined && (
                    <span className="text-sm text-gray-500">
                      {option.commission}%
                    </span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
